import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import Styles from './IFToggle.module.css'
import styled from 'styled-components'
import IFText from 'Components/IFText/IFText'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { isPropsMatch } from 'Utils/PropsMatch'
import { styled as muiStyled, keyframes } from '@mui/system'

const getTextWidth = (txt, font) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  context.font = font
  return context.measureText(txt).width
}

const ratioToggleHeight = 0.85
const ratioTopHeight = 0.075

const CustomLabel = styled.label`
  position: relative;
  z-index: 1; /* Sit underneath the checked/unchecked containers so text/icons stay visible */
  display: block;
  background: ${(props) => props.backgroundColor + Colors.HexTransparent.T85};

  &:hover {
    background: ${(props) => props.backgroundColor};
  }

  /* The "knob" that slides left/right is implemented as an ::after pseudoelement */
  &:after {
    content: '';
    position: absolute;
    height: ${(props) => props.height}px;
    width: ${(props) => props.height}px;
    top: ${(props) => props.top}px;
    left: ${(props) => props.top}px;
    background: ${Colors.white};
    background-image: ${(props) =>
      props.checkedText
        ? props.isChecked
          ? `url('data:image/svg+xml,${props.encodedCheckedIcon}')`
          : `url('data:image/svg+xml,${props.encodedUncheckedIcon}')`
        : null};
    background-repeat: no-repeat;
    background-position: center;
    transition: left 0.15s ease-in-out;
  }

  /* While active (pressed), we briefly expand the knob's width for a press effect */
  &:active:after {
    width: ${(props) => props.toggleActive}px;
  }
`

const CustomInput = styled.input`
  display: none;

  &:checked + label:after {
    left: calc(100% - ${(props) => props.margin}px);
  }
`

const animateCheck = (height) =>
  keyframes({
    '0%': { opacity: 0 },
    '30%': { opacity: 0 },
    '50%': { opacity: 1 },
    '100%': {
      opacity: 1,
      marginRight: height * ratioToggleHeight,
    },
  })

const animateCheckReverse = (height) =>
  keyframes({
    '0%': { opacity: 0 },
    '45%': { opacity: 0 },
    '65%': { opacity: 1 },
    '100%': {
      opacity: 1,
      marginRight: height * ratioToggleHeight,
    },
  })

const animateUncheck = (height) =>
  keyframes({
    '0%': { opacity: 1 },
    '30%': { opacity: 1 },
    '50%': { opacity: 0 },
    '100%': {
      opacity: 0,
      marginRight: height * ratioToggleHeight,
    },
  })

const animateUncheckReverse = (height) =>
  keyframes({
    '0%': { opacity: 1 },
    '45%': { opacity: 1 },
    '65%': { opacity: 0 },
    '100%': {
      opacity: 0,
      marginRight: height * ratioToggleHeight,
    },
  })

const CheckedContainer = muiStyled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'height' && prop !== 'animate' && prop !== 'isChecked',
})(({ height, animate, isChecked }) => ({
  position: 'absolute',
  zIndex: 2,
  animation:
    animate === 'check'
      ? `${animateCheck(height)} 0.15s normal forwards`
      : animate === 'checkReverse'
      ? `${animateCheckReverse(height)} 0.15s reverse forwards`
      : 'none',
  opacity: animate ? undefined : isChecked ? 1 : 0,
  marginRight: animate ? undefined : isChecked ? height * ratioToggleHeight : 0,
}))

const UncheckedContainer = muiStyled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'height' && prop !== 'animate' && prop !== 'isChecked',
})(({ height, animate, isChecked }) => ({
  position: 'absolute',
  zIndex: 2,
  animation:
    animate === 'uncheck'
      ? `${animateUncheck(height)} 0.15s normal forwards`
      : animate === 'uncheckReverse'
      ? `${animateUncheckReverse(height)} 0.15s reverse forwards`
      : 'none',
  opacity: animate ? undefined : isChecked ? 0 : 1,
  marginRight: 0,
}))

const IFToggle = React.forwardRef(
  (
    {
      fontWeight = 'normal',
      height = 20,
      fontSize = 12,
      defaultChecked = false,
      readOnly = false,
      CheckedIcon,
      UncheckedIcon,
      checkedText = '',
      uncheckedText = '',
      checkedTextColor = Colors.white,
      uncheckedTextColor = Colors.white,
      checkedColor = Colors.primary,
      uncheckedColor = Colors.ToggleDisabled,
      marginLeftRight = 6,
      checked,
      onChange = () => {},
    },
    ref,
  ) => {
    const [isChecked, setIsChecked] = useState(defaultChecked)
    const [isClicked, setIsClicked] = useState(false)
    const checkedIconRef = useRef()
    const uncheckedIconRef = useRef()
    const [checkedIconWidth, setCheckedIconWidth] = useState(0)
    const [uncheckedIconWidth, setUnCheckedIconWidth] = useState(0)

    const id = 'switch' + uuidv4()

    useEffect(() => {
      if (checked === true || checked === false) {
        setIsChecked(checked)
      }
    }, [checked])

    useEffect(() => {
      onChange(isChecked)
    }, [isChecked])

    useEffect(() => {
      setIsClicked(false)
    }, [readOnly])

    useEffect(() => {
      if (checkedIconRef.current) {
        setCheckedIconWidth(checkedIconRef.current.clientWidth)
      }
    }, [checkedIconRef.current])

    useEffect(() => {
      if (uncheckedIconRef.current) {
        setUnCheckedIconWidth(uncheckedIconRef.current.clientWidth)
      }
    }, [uncheckedIconRef.current])

    const getValue = () => isChecked
    const toggleValue = () => setIsChecked((prev) => !prev)
    const setValue = (value) => setIsChecked(value)

    useImperativeHandle(ref, () => ({
      getValue,
      toggleValue,
      setValue,
    }))

    // Compute dynamic width:
    // left padding (top‐height) + toggle‐knob area + marginLeft + text/icon width + marginRight
    const contentWidth = isChecked
      ? checkedText
        ? getTextWidth(checkedText, `${fontWeight} ${fontSize}px ProximaNova`)
        : checkedIconWidth
      : uncheckedText
      ? getTextWidth(uncheckedText, `${fontWeight} ${fontSize}px ProximaNova`)
      : uncheckedIconWidth

    const totalWidth =
      height * ratioTopHeight +
      height * ratioToggleHeight +
      marginLeftRight +
      contentWidth +
      marginLeftRight

    return (
      <div
        className={
          isClicked
            ? `${Styles.Container} ${Styles.transitionWidth}`
            : Styles.Container
        }
        style={{
          width: totalWidth,
          alignItems: 'center',
        }}
      >
        <CheckedContainer
          height={height}
          isChecked={isChecked}
          animate={isClicked ? (isChecked ? 'check' : 'checkReverse') : null}
          style={{
            right: marginLeftRight,
          }}
        >
          {checkedText ? (
            <IFText
              style={{
                color: checkedTextColor,
                fontSize,
                fontWeight,
              }}
            >
              {checkedText}
            </IFText>
          ) : (
            <div ref={checkedIconRef} className={Styles.IconContainer}>
              {CheckedIcon}
            </div>
          )}
        </CheckedContainer>
        <UncheckedContainer
          height={height}
          isChecked={isChecked}
          animate={
            isClicked ? (isChecked ? 'uncheck' : 'uncheckReverse') : null
          }
          style={{
            right: marginLeftRight,
          }}
        >
          {uncheckedText ? (
            <IFText
              style={{
                color: uncheckedTextColor,
                fontSize,
                fontWeight,
              }}
            >
              {uncheckedText}
            </IFText>
          ) : (
            <div ref={uncheckedIconRef} className={Styles.IconContainer}>
              {UncheckedIcon}
            </div>
          )}
        </UncheckedContainer>
        <CustomInput
          className={Styles.Input}
          type="checkbox"
          autoFocus={false}
          id={id}
          checked={isChecked}
          onChange={(e) => {
            if (!readOnly) {
              setIsClicked(true)
              setIsChecked(e.target.checked)
            }
          }}
          disabled={readOnly}
          margin={height * ratioTopHeight}
        />
        <CustomLabel
          height={height * ratioToggleHeight}
          top={height * ratioTopHeight}
          className={
            isClicked
              ? `${Styles.Label} ${Styles.transitionWidthBackground}`
              : Styles.Label
          }
          encodedCheckedIcon={CheckedIcon}
          encodedUncheckedIcon={UncheckedIcon}
          isChecked={isChecked}
          checkedText={checkedText}
          toggleActive={height * ratioToggleHeight + height * ratioTopHeight}
          backgroundColor={isChecked ? checkedColor : uncheckedColor}
          style={{
            height,
            width: totalWidth,
          }}
          htmlFor={id}
        />
      </div>
    )
  },
)

IFToggle.propTypes = {
  height: PropTypes.number,
  fontSize: PropTypes.number,
  defaultChecked: PropTypes.bool,
  readOnly: PropTypes.bool,
  CheckedIcon: PropTypes.element,
  UncheckedIcon: PropTypes.element,
  checkedText: PropTypes.string,
  uncheckedText: PropTypes.string,
  checkedTextColor: PropTypes.string,
  uncheckedTextColor: PropTypes.string,
  checkedColor: PropTypes.string,
  uncheckedColor: PropTypes.string,
  marginLeftRight: PropTypes.number,
  checked: PropTypes.bool,
  fontWeight: PropTypes.string,
  onChange: PropTypes.func,
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'height',
    'fontSize',
    'readOnly',
    'CheckedIcon',
    'UncheckedIcon',
    'checkedText',
    'uncheckedText',
    'checkedTextColor',
    'uncheckedTextColor',
    'checkedColor',
    'uncheckedColor',
    'marginLeftRight',
    'checked',
    'fontWeight',
  ])
}

export default React.memo(IFToggle, shouldSkipRender)
